// src/services/auth.js
import { withTimeout, timeoutError } from './api/client';

const XANO_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.rehabilitationhealth.com/api:RIB0KqtX';
const VERADIGM_TOKEN_KEY = 'veradigm_token'; // not using, we should remove associated functionality
const VERADIGM_TIMESTAMP_KEY = 'veradigm_token_timestamp'; // not using, we should remove associated functionality

// A request timeout (services/api/client.js withTimeout, name "TimeoutError")
// says nothing about the token, only about the backend right now, so the
// session logic treats it exactly like a dropped connection: keep the
// session, try again later. Without this, a stalled auth/me under load
// cleared every tab's token and every tab re-exchanged with Auth0 at the
// worst moment (review finding, 2026-09-22).
export function isNetworkError(error) {
  if (error?.name === 'TimeoutError') return true;
  return (
    error instanceof TypeError &&
    (error.message === 'Failed to fetch' ||
     error.message === 'NetworkError when attempting to fetch resource.' ||
     error.message === 'Load failed')
  );
}

// auth/me can answer 200 with an empty/unusable body when the token
// authenticates but no user row exists. Treat that as a terminal 404 so the
// caller's terminal branch runs instead of retrying forever on a null profile.
function assertProfile(profile) {
  if (!profile || typeof profile !== 'object' || !profile.id) {
    const error = new Error('Profile not found');
    error.status = 404;
    throw error;
  }
}

class AuthService {
  constructor() {
    this.token = null;
    this.lastValidation = null;
    this.VALIDATION_WINDOW = 5 * 60 * 1000; // 5 minutes
    this._getAccessTokenSilently = null;
    this._getIdTokenClaims = null;
    this._refreshPromise = null;
  }

  // Registered from UserContext once the Auth0 hooks are available, so the
  // non-React API client can trigger silent token refreshes.
  registerAuth0Helpers(getAccessTokenSilently, getIdTokenClaims) {
    this._getAccessTokenSilently = getAccessTokenSilently;
    this._getIdTokenClaims = getIdTokenClaims;
  }

  // Single-flight silent refresh: concurrent callers share one exchange.
  async refreshXanoToken() {
    if (this._refreshPromise) {
      return this._refreshPromise;
    }
    this._refreshPromise = this._doRefreshXanoToken().finally(() => {
      this._refreshPromise = null;
    });
    return this._refreshPromise;
  }

  async _doRefreshXanoToken() {
    if (!this._getAccessTokenSilently || !this._getIdTokenClaims) {
      throw new Error('Auth0 helpers not registered');
    }
    try {
      const accessToken = await this._getAccessTokenSilently();
      const idTokenClaims = await this._getIdTokenClaims();
      if (!idTokenClaims?.__raw) {
        throw new Error('Failed to get ID token claims');
      }
      await this.exchangeAuth0Tokens(accessToken, idTokenClaims.__raw);
      return this.getToken();
    } catch (error) {
      // Terminal Auth0 failures mean silent recovery is impossible — surface a
      // visible re-auth path instead of leaving a silently dead client.
      const terminalCodes = ['missing_refresh_token', 'login_required', 'invalid_grant', 'consent_required'];
      if (terminalCodes.includes(error?.error)) {
        window.dispatchEvent(new Event('auth:session-expired'));
      }
      throw error;
    }
  }

  // Raw fetch (outside the request queue) with a per-attempt timeout
  // (2026-09-22): without one a stalled auth/me or token exchange hung the
  // session check forever. Only dropped connections are retried here; a
  // timeout propagates (callers classify it as retryable and keep the session).
  async _fetchWithRetry(url, options = {}, { maxAttempts = 3, baseDelay = 1000, timeoutMs = 30000 } = {}) {
    let lastError;
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      const timeout = withTimeout(options.signal, timeoutMs);
      try {
        return await fetch(url, { ...options, signal: timeout.signal });
      } catch (error) {
        const err = timeout.didTimeOut() ? timeoutError() : error;
        lastError = err;
        if (err.name === 'TimeoutError' || !isNetworkError(err) || attempt === maxAttempts) {
          throw err;
        }
        const delay = baseDelay * Math.pow(2, attempt - 1) + Math.random() * 500;
        await new Promise(resolve => setTimeout(resolve, delay));
      } finally {
        timeout.clear();
      }
    }
    throw lastError;
  }

  setToken(token) {
    if (!token) {
      console.warn('[Auth] Attempted to set null/undefined token');
      return;
    }
    this.token = token;
    this.lastValidation = Date.now();
    localStorage.setItem('xano_token', token);
    localStorage.setItem('xano_token_minted_at', Date.now().toString());
  }

  // Age of the current Xano token; the backend mints them with a fixed 24h
  // expiration, so age is the only client-side expiry signal.
  getTokenAgeMs() {
    if (!this.getToken()) return null;
    let mintedAt = parseInt(localStorage.getItem('xano_token_minted_at'), 10);
    if (!mintedAt) {
      mintedAt = Date.now();
      localStorage.setItem('xano_token_minted_at', mintedAt.toString());
    }
    return Date.now() - mintedAt;
  }

  getToken() {
    if (!this.token) {
      this.token = localStorage.getItem('xano_token');
    }
    return this.token;
  }

  clearToken() {
    this.token = null;
    this.lastValidation = null;
    localStorage.removeItem('xano_token');
    localStorage.removeItem('xano_token_minted_at');
    localStorage.removeItem(VERADIGM_TOKEN_KEY);
    localStorage.removeItem(VERADIGM_TIMESTAMP_KEY);
    localStorage.removeItem('prism-preferred-mode');
  }

  needsValidation() {
    if (!this.lastValidation) return true;
    const elapsed = Date.now() - this.lastValidation;
    return elapsed > this.VALIDATION_WINDOW;
  }

  async validateTokens(getAccessTokenSilently, getIdTokenClaims) {
    const token = this.getToken();
    
    if (!token) {
      return { valid: false, reason: 'no-token' };
    }

    // If within validation window and we have a token, consider it valid
    if (!this.needsValidation()) {
      return { valid: true };
    }

    try {
      // Validate existing Xano token
      const response = await this._fetchWithRetry(`${XANO_BASE_URL}/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });

      if (response.ok) {
        const profile = await response.json();
        assertProfile(profile);
        this.lastValidation = Date.now();
        return { valid: true, profile };
      }

      // A server-side failure says nothing about the token — keep the session
      // and let the next interval tick retry.
      if (response.status >= 500) {
        return { valid: false, reason: 'server-error', retryable: true };
      }

      // Token rejected — try a silent refresh through the single-flight path
      if (this._getAccessTokenSilently || getAccessTokenSilently) {
        try {
          if (getAccessTokenSilently && getIdTokenClaims) {
            this.registerAuth0Helpers(getAccessTokenSilently, getIdTokenClaims);
          }
          await this.refreshXanoToken();

          // Verify the new token works
          const newResponse = await this._fetchWithRetry(`${XANO_BASE_URL}/auth/me`, {
            headers: {
              'Authorization': `Bearer ${this.getToken()}`,
              'Accept': 'application/json'
            }
          });

          if (newResponse.ok) {
            const newProfile = await newResponse.json();
            assertProfile(newProfile);
            this.lastValidation = Date.now();
            return { valid: true, profile: newProfile };
          }
        } catch (refreshError) {
          console.error('[Auth] Token refresh failed:', refreshError);
          if (isNetworkError(refreshError)) {
            return { valid: false, reason: 'network-error', retryable: true };
          }
          // Missing profile is terminal — surface it to the caller
          if (refreshError.status === 404) throw refreshError;
        }
      }
      this.clearToken();
      return { valid: false, reason: 'invalid-token' };
    } catch (error) {
      console.error('[Auth] Token validation error:', error);
      if (isNetworkError(error)) {
        return { valid: false, reason: 'network-error', retryable: true };
      }
      this.clearToken();
      // Missing profile is terminal — let initializeUser's terminal branch run
      if (error.status === 404) throw error;
      return { valid: false, reason: 'validation-error' };
    }
  }

  async exchangeAuth0Tokens(accessToken, idToken) {
    if (!accessToken || !idToken) {
      throw new Error('Missing required tokens for exchange');
    }

    try {
      const url = new URL(`${XANO_BASE_URL}/oauth/auth0/login`);
      url.searchParams.append('access_token', accessToken);
      url.searchParams.append('id_token', idToken);

      const response = await this._fetchWithRetry(url.toString(), {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[Auth] Token exchange failed:', errorText);
        // Keep the existing token — it may still be valid (e.g. a proactive
        // renewal that failed transiently); callers decide how to recover.
        throw new Error('Failed to exchange tokens with Xano');
      }

      const data = await response.json();
      
      if (!data.token) {
        throw new Error('No token received from Xano');
      }

      this.setToken(data.token);
      
      return data;
    } catch (error) {
      console.error('[Auth] Token exchange error:', error);
      throw error;
    }
  }

  async getUserProfile() {
    const token = this.getToken();

    if (!token) {
      throw new Error('No authentication token found');
    }

    try {
      const response = await this._fetchWithRetry(`${XANO_BASE_URL}/auth/me`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        // Only an auth rejection invalidates the token — a transient 5xx does not
        if (response.status === 401 || response.status === 403) {
          this.clearToken();
        }
        const error = new Error('Failed to get user profile');
        error.status = response.status;
        throw error;
      }

      const profile = await response.json();
      assertProfile(profile);
      return profile;
    } catch (error) {
      if (error.status === 401 || error.status === 403) {
        this.clearToken();
      }
      throw error;
    }
  }
}

export const authService = new AuthService();